import clsx from 'clsx';
import { useEffect, useRef, type ReactNode } from 'react';
import type { DisplayMode } from '../types';
import styles from './Drawer.module.css';

const STORAGE_KEY = 'phia:drawer-width';
const MIN_WIDTH = 320;
const MAX_WIDTH = 720;

function applyWidth(root: HTMLElement | null, px: number, pushOpen = false) {
  const value = `${px}px`;
  if (root) root.style.setProperty('--phia-drawer-width', value);
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty('--phia-drawer-width', value);
    // Body padding-right has to be a real px value, not 'var(--…)'. The
    // host may use box-sizing / layouts that don't resolve our CSS var
    // (or strip it for safety), so we resolve it here in JS.
    if (pushOpen) document.body.style.paddingRight = value;
  }
}

function readStoredWidth(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n >= MIN_WIDTH && n <= MAX_WIDTH ? n : null;
  } catch {
    return null;
  }
}

type Props = {
  open: boolean;
  onClose: () => void;
  shadowRoot: ShadowRoot;
  mode: DisplayMode;
  /** When true the drawer ignores `mode` and renders fullscreen. */
  fullscreen?: boolean;
  headerLeft?: ReactNode;
  headerActions?: ReactNode;
  children: ReactNode;
};

/**
 * Side panel with three presentation modes (driven by AppParams.displayMode):
 *   - 'push'    → nudges <body> leftward with padding-right so the host
 *                 stays visible and interactive. No backdrop.
 *   - 'overlay' → drawer floats above the host on top of a dim backdrop;
 *                 clicking the backdrop or pressing Escape closes it.
 *   - 'iframe'  → widget fills the viewport (100vw/100vh). When opened it
 *                 also attempts requestFullscreen on the host element so
 *                 it can escape a small embedding iframe (SCORM/Moodle).
 */
export function Drawer({
  open,
  onClose,
  shadowRoot,
  mode,
  fullscreen,
  headerLeft,
  headerActions,
  children
}: Props) {
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const effectiveMode: DisplayMode = fullscreen ? 'iframe' : mode;
  const resizable = effectiveMode === 'push' || effectiveMode === 'overlay';

  // On first mount of a resizable drawer, rehydrate the stored width.
  useEffect(() => {
    if (!resizable) return;
    const stored = readStoredWidth();
    if (stored == null) return;
    const root = shadowRoot.querySelector<HTMLElement>('.phia-root');
    applyWidth(root, stored);
  }, [resizable, shadowRoot]);

  const onResizeDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const root = shadowRoot.querySelector<HTMLElement>('.phia-root');
    const current = root
      ? parseFloat(getComputedStyle(root).getPropertyValue('--phia-drawer-width')) ||
        440
      : 440;
    resizeRef.current = { startX: e.clientX, startWidth: current };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  };
  const onResizeMove = (e: React.PointerEvent) => {
    if (!resizeRef.current) return;
    const delta = resizeRef.current.startX - e.clientX; // dragging left grows
    const next = Math.max(
      MIN_WIDTH,
      Math.min(MAX_WIDTH, Math.round(resizeRef.current.startWidth + delta))
    );
    const root = shadowRoot.querySelector<HTMLElement>('.phia-root');
    applyWidth(root, next, effectiveMode === 'push');
  };
  const onResizeUp = (e: React.PointerEvent) => {
    if (!resizeRef.current) return;
    const delta = resizeRef.current.startX - e.clientX;
    const next = Math.max(
      MIN_WIDTH,
      Math.min(MAX_WIDTH, Math.round(resizeRef.current.startWidth + delta))
    );
    resizeRef.current = null;
    (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    try {
      window.localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      /* private mode etc. — ignore */
    }
    // Tell the host page (e.g. the playground) so it can sync its UI.
    window.dispatchEvent(
      new CustomEvent('phia:drawer-resize', { detail: { width: next } })
    );
  };
  // Push the host content sideways only in 'push' mode.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (effectiveMode !== 'push') return;
    const isDesktop = window.matchMedia('(min-width: 641px)').matches;
    if (!isDesktop) return;

    const body = document.body;
    const prevPadding = body.style.paddingRight;
    const prevTransition = body.style.transition;
    body.style.transition = 'padding-right 360ms cubic-bezier(0.16, 1, 0.3, 1)';
    if (open) {
      const w =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue(
            '--phia-drawer-width'
          )
        ) || 440;
      body.style.paddingRight = `${w}px`;
    } else {
      body.style.paddingRight = '';
    }
    return () => {
      body.style.paddingRight = prevPadding;
      body.style.transition = prevTransition;
    };
  }, [open, effectiveMode]);

  // Exit fullscreen automatically when the drawer closes.
  useEffect(() => {
    if (effectiveMode !== 'iframe' || open) return;
    const doc = document as Document & {
      webkitFullscreenElement?: Element;
      webkitExitFullscreen?: () => Promise<void>;
    };
    if (!doc.fullscreenElement && !doc.webkitFullscreenElement) return;
    const exit =
      document.exitFullscreen?.bind(document) ?? doc.webkitExitFullscreen?.bind(document);
    Promise.resolve(exit?.()).catch(() => {});
  }, [open, effectiveMode]);

  // Close on Escape regardless of mode.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Click-outside closes the drawer in push mode. Overlay already handles
  // this via its backdrop; iframe fills the viewport so there is no outside.
  // We can't rely on `event.target` because shadow-DOM event retargeting
  // collapses it to the host element — composedPath() shows the real path.
  useEffect(() => {
    if (!open || effectiveMode !== 'push') return;
    const onDown = (e: PointerEvent) => {
      if (e.composedPath().includes(shadowRoot.host)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, effectiveMode, onClose, shadowRoot]);

  return (
    <>
      {effectiveMode === 'overlay' && (
        <div
          className={clsx(styles.backdrop, open && styles.backdropOpen)}
          onClick={onClose}
          aria-hidden={!open}
        />
      )}
      <aside
        className={clsx(
          styles.drawer,
          open && styles.open,
          effectiveMode === 'overlay' && styles.drawerOverlay,
          effectiveMode === 'iframe' && styles.drawerFullscreen
        )}
        role="complementary"
        aria-label="Tutor AI"
        aria-hidden={!open}
      >
        {resizable && open && (
          <div
            className={styles.resizer}
            role="separator"
            aria-orientation="vertical"
            aria-label="Redimensionar"
            onPointerDown={onResizeDown}
            onPointerMove={onResizeMove}
            onPointerUp={onResizeUp}
            onPointerCancel={onResizeUp}
          >
            <span className={styles.resizerGrip} aria-hidden />
          </div>
        )}
        <header className={styles.header}>
          <div className={styles.headerLeft}>{headerLeft}</div>
          <div className={styles.headerActions}>{headerActions}</div>
        </header>
        <div className={styles.body}>{children}</div>
      </aside>
    </>
  );
}
